import {useRouter} from 'next/router';
import React, {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {requestContext} from '../../../common/requestContext';
import {ApiStatusCodeVo} from '../../../common/shared/api/nicolas/vo/enums/ApiStatusCodeVo.enum';
import {
  REDEEM_REGEX_3,
  REDEEM_REGEX_4,
  REDEEM_REGEX_12,
  REDEEM_REGEX_14,
  REDEEM_REGEX_15,
  REDEEM_REGEX_16,
} from '../constant';
import {useSubmitRedeem} from '../hook/useSubmitRedeem';
import {RedeemProps} from '../presentational/Redeem';
import {Dialog} from '../type';
import {getRedeemInfoList} from '../util/getRedeemInfoList';
import {goToUrlByRedeemCodeType} from '../util/goToUrlByRedeemCodeType';

interface Props {
  children: (props: RedeemProps) => ReactNode;
}

export function RedeemContainer({children}: Props) {
  const router = useRouter();
  const {isWebView, isMobile, isAndroid} = requestContext.requestInfo.display;
  const isGlobal = requestContext.requestInfo.isGlobal;
  const [redeemCode, setRedeemCode] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [dialog, setDialog] = useState<Dialog>();

  const handleChangeRedeemCode = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setRedeemCode(event.target.value);
    },
    []
  );

  const handlePasteRedeemCode = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      // 알림톡같은 전체 문장을 붙여넣기 할 경우, 쿠폰 번호만 붙여넣기
      const clipboardData = e.clipboardData.getData('text');
      const inputValue =
        clipboardData.match(REDEEM_REGEX_16) ??
        clipboardData.match(REDEEM_REGEX_4) ??
        clipboardData.match(REDEEM_REGEX_15) ??
        clipboardData.match(REDEEM_REGEX_14) ??
        clipboardData.match(REDEEM_REGEX_12) ??
        clipboardData.match(REDEEM_REGEX_3);

      if (inputValue) {
        setRedeemCode(inputValue[0].toUpperCase());
      }
      e.preventDefault();
    },
    []
  );

  const {handleSubmitRedeemCode, data, isFetching} = useSubmitRedeem({
    redeemCode,
    setShowDialog,
    setDialog,
  });

  const handleClickRemoveButton = useCallback(() => setRedeemCode(''), []);

  const handleClickSecondButton = useCallback(() => {
    if (data?.resultCode === ApiStatusCodeVo.Success) {
      setRedeemCode('');
    }
    setShowDialog(false);
  }, [data?.resultCode]);

  const handleClickActiveButton = useCallback(async () => {
    if (data?.resultCode === ApiStatusCodeVo.Success) {
      setRedeemCode('');
    }
    goToUrlByRedeemCodeType({
      redeemCodeType: data?.type,
      seriesId: data?.seriesId,
      code: redeemCode,
      resultCode: data?.resultCode,
      isWebView,
      isAndroid,
      onSubmitRedeemCode: handleSubmitRedeemCode,
    });
    setShowDialog(false);
  }, [redeemCode, data, isAndroid, isWebView, handleSubmitRedeemCode]);

  useEffect(() => {
    if (router.isReady && router.query.code) {
      setRedeemCode(String(router.query.code));
    }
  }, [router]);

  return (
    <>
      {children({
        isMobile,
        redeemCode,
        showDialog,
        dialog,
        disabledRegisterButton: isFetching,
        redeemInfoList: getRedeemInfoList(isGlobal),
        onChangeRedeemCode: handleChangeRedeemCode,
        onPasteRedeemCode: handlePasteRedeemCode,
        onClickRemoveButton: handleClickRemoveButton,
        onSubmitRedeemCode: handleSubmitRedeemCode,
        onCloseDialog: () => setShowDialog(false),
        onClickSecondButton: handleClickSecondButton,
        onClickActiveButton: handleClickActiveButton,
      })}
    </>
  );
}
